// Fetcher functions + React Query hooks for the Treasury Curve dashboard.
//
// Auth: we reuse the project's shared authenticated Axios instance
// (`apiClient` from src/api/client.ts). It is a NAMED export whose
// baseURL already includes the `/api/v1` version prefix and attaches the
// WordPress-issued JWT (`Authorization: Bearer <token>`) + the
// `forge_session` cookie on every request, and centralises 401 → silent
// refresh → WP-login-bounce handling. Because the `/api/v1` segment is
// baked into baseURL, our BASE_URL below is the BARE domain path only.

import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../../../../api/client';
import type {
    AnalyticsTimeseriesPoint,
    TimeTravelPoint,
    TreasuryGridResponse,
    TreasurySnapshot,
    RateSeriesPoint,      // NEW
    YieldCurvePoint,      // NEW
    // NEW IMPORTS:
    RealYieldSnapshot,
    RealYieldTimeseriesPoint,
    StockRealYieldCorrelationPoint,
} from '../types';

// NOTE: apiClient.baseURL already contains `/api/v1`, so we must NOT repeat
// it here or every request would 404 on `/api/v1/api/v1/...`.
const BASE_URL = '/market-analysis/treasury-curve';

// --- FETCHERS -----------------------------------------------------------
export const fetchTreasurySnapshot = async (): Promise<TreasurySnapshot> => {
    const { data } = await apiClient.get<TreasurySnapshot>(`${BASE_URL}/snapshot`);
    return data;
};

export const fetchTimeTravelCurves = async (): Promise<TimeTravelPoint[]> => {
    const { data } = await apiClient.get<TimeTravelPoint[]>(`${BASE_URL}/time-travel`);
    return data;
};

export const fetchAnalyticsTimeseries = async (
    years = 3,
    fields?: string, // e.g., 'spread_10y_2y_bps,market_regime'
): Promise<AnalyticsTimeseriesPoint[]> => {
    const { data } = await apiClient.get<AnalyticsTimeseriesPoint[]>(
        `${BASE_URL}/timeseries`,
        { params: { years, fields } },
    );
    return data;
};

export const fetchTreasuryGrid = async (
    limit = 100,
    offset = 0,
    regimes?: string,
    fromDate?: string,
    toDate?: string,
): Promise<TreasuryGridResponse> => {
    const { data } = await apiClient.get<TreasuryGridResponse>(`${BASE_URL}/grid`, {
        // Axios will automatically drop undefined parameters
        params: { limit, offset, regimes, from_date: fromDate, to_date: toDate },
    });
    return data;
};

// --- HOOKS --------------------------------------------------------------
export const useTreasurySnapshot = () => {
    return useQuery({
        queryKey: ['treasury', 'snapshot'],
        queryFn: fetchTreasurySnapshot,
        // Treasury data updates once daily, so we cache heavily to save API calls.
        staleTime: 1000 * 60 * 60, // 1 hour
    });
};

export const useTimeTravelCurves = () => {
    return useQuery({
        queryKey: ['treasury', 'time-travel'],
        queryFn: fetchTimeTravelCurves,
        staleTime: 1000 * 60 * 60,
    });
};

export const useAnalyticsTimeseries = (years = 3, fields?: string) => {
    return useQuery({
        // Include fields in the queryKey so React Query knows when to refetch
        queryKey: ['treasury', 'timeseries', years, fields],
        queryFn: () => fetchAnalyticsTimeseries(years, fields),
        staleTime: 1000 * 60 * 60,
    });
};

export const useTreasuryGrid = (
    limit: number,
    offset: number,
    regimes?: string,
    fromDate?: string,
    toDate?: string,
) => {
    return useQuery({
        queryKey: ['treasury', 'grid', limit, offset, regimes, fromDate, toDate],
        queryFn: () => fetchTreasuryGrid(limit, offset, regimes, fromDate, toDate),
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

// --- NEW FETCHERS AND HOOKS ---

export const fetchRatesSeries = async (
    years = 3,
    tenors?: string, // e.g., 'month1,year2,year10'
    includeRegime = false,
): Promise<RateSeriesPoint[]> => {
    const { data } = await apiClient.get<RateSeriesPoint[]>(`${BASE_URL}/rates/series`, {
        params: { years, tenors, include_regime: includeRegime },
    });
    return data;
};

export const fetchRatesCurve = async (
    targetDate?: string,
): Promise<YieldCurvePoint> => {
    const { data } = await apiClient.get<YieldCurvePoint>(`${BASE_URL}/rates/curve`, {
        params: { target_date: targetDate },
    });
    return data;
};

export const useRatesSeries = (years = 3, tenors?: string, includeRegime = false) => {
    return useQuery({
        queryKey: ['treasury', 'rates-series', years, tenors, includeRegime],
        queryFn: () => fetchRatesSeries(years, tenors, includeRegime),
        staleTime: 1000 * 60 * 60,
    });
};

export const useRatesCurve = (targetDate?: string) => {
    return useQuery({
        // If targetDate is undefined, it just fetches the latest day
        queryKey: ['treasury', 'rates-curve', targetDate || 'latest'],
        queryFn: () => fetchRatesCurve(targetDate),
        staleTime: 1000 * 60 * 60 * 24, // Past curves never change, cache heavily
    });
};

// --- NEW REAL YIELD FETCHERS ---

export const fetchRealYieldSnapshot = async (): Promise<RealYieldSnapshot> => {
    const { data } = await apiClient.get<RealYieldSnapshot>(
        `${BASE_URL}/analytics/real-yield/snapshot`,
    );
    return data;
};

export const fetchRealYieldTimeseries = async (
    years = 5,
): Promise<RealYieldTimeseriesPoint[]> => {
    const { data } = await apiClient.get<RealYieldTimeseriesPoint[]>(
        `${BASE_URL}/analytics/real-yield`,
        { params: { years } },
    );
    return data;
};

export const fetchRealYieldEquityCorrelation = async (
    years = 5,
): Promise<StockRealYieldCorrelationPoint[]> => {
    const { data } = await apiClient.get<StockRealYieldCorrelationPoint[]>(
        `${BASE_URL}/analytics/real-yield/equities`,
        { params: { years } },
    );
    return data;
};

// --- NEW REAL YIELD HOOKS ---

export const useRealYieldSnapshot = () => {
    return useQuery({
        queryKey: ['treasury', 'real-yield-snapshot'],
        queryFn: fetchRealYieldSnapshot,
        staleTime: 1000 * 60 * 60,
    });
};

export const useRealYieldTimeseries = (years = 5) => {
    return useQuery({
        queryKey: ['treasury', 'real-yield-timeseries', years],
        queryFn: () => fetchRealYieldTimeseries(years),
        staleTime: 1000 * 60 * 60,
    });
};

export const useRealYieldEquityCorrelation = (years = 5) => {
    return useQuery({
        queryKey: ['treasury', 'real-yield-correlation', years],
        queryFn: () => fetchRealYieldEquityCorrelation(years),
        staleTime: 1000 * 60 * 60,
    });
};
